import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiFolderAdd } from "react-icons/hi";
import { createFolderAPI } from "../services/driveService";
import { toast } from "react-toastify";
import { useActivity } from "../ActivityContext";

const CreateFolderModal = ({ isOpen, onClose, onSuccess, parentId = null, isPrivate = false }) => {
  const { refreshActivityReport } = useActivity();
  const [folderName, setFolderName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    setLoading(true);
    try {
      await createFolderAPI(folderName, parentId, isPrivate);
      toast.success("Folder created successfully!");
      setFolderName("");
      refreshActivityReport();
      window.dispatchEvent(new CustomEvent("folderCreated"));
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white/80 backdrop-blur-2xl border border-white rounded-[2.5rem] shadow-2xl overflow-hidden p-8"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-inner">
                  <HiFolderAdd size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 tracking-tight">New Folder</h3>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">Create a space</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
              >
                <HiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative group">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                  Folder Name
                </label>
                <input
                  autoFocus
                  type="text"
                  placeholder="e.g. Project Assets"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none text-slate-700 font-medium placeholder:text-slate-300"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-4 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !folderName.trim()}
                  className="flex-[1.5] py-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-2xl shadow-xl shadow-indigo-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    "Create Folder"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CreateFolderModal;
